import { useEffect, useState } from "react";
import { Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { Loading, Panel } from "../common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const P = (v) => String(+(v * 100).toFixed(4));
const years = Array.from({ length: 16 }, (_, i) => 2016 + i);

function PctField({ label, value, onChange, testid, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="relative"><Input type="number" step="any" min="0" max="100" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="pr-8" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span></div>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function PartnerList({ partners, reload }) {
  const [name, setName] = useState("");
  const [edits, setEdits] = useState({});
  const add = async () => {
    try { await api.post("/settings/partners", { name }); setName(""); toast.success("Partner added"); reload(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (p) => {
    const n = edits[p.id];
    if (!n || n === p.name) return;
    try { await api.patch(`/settings/partners/${p.id}`, { name: n }); toast.success("Partner renamed"); reload(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel eyebrow="Settings" title="JV partner list" testid="settings-partners">
      <div className="mb-4 flex gap-2"><Input placeholder="New partner name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-partner-input" />
        <Button onClick={add} disabled={!name.trim()} data-testid="add-partner-btn"><Plus className="mr-1 h-4 w-4" /> Add</Button></div>
      <ul className="grid gap-2 sm:grid-cols-2">{partners.map((p) => (
        <li key={p.id}><Input defaultValue={p.name} onChange={(e) => setEdits({ ...edits, [p.id]: e.target.value })} onBlur={() => rename(p)} data-testid={`partner-name-input-${p.id}`} /></li>
      ))}</ul>
    </Panel>
  );
}

export function SettingsTab() {
  const { data, loading, reload } = useApi("/settings");
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!data) return;
    const [y, q] = data.report_start.split("-Q");
    setF({ year: y, quarter: q, amsm_am_equity: P(data.amsm_am_equity), amsm_am_profit: P(data.amsm_am_profit), fo_pct_amsm: P(data.fo_pct_amsm), fo_pct_am: P(data.fo_pct_am),
      owner_codes: data.owner_codes.map((c) => ({ ...c, am_equity: P(c.am_equity), am_profit: P(c.am_profit) })) });
  }, [data]);
  if ((loading && !data) || !f) return <Loading />;
  const setCode = (i, k, v) => setF({ ...f, owner_codes: f.owner_codes.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        report_start: `${f.year}-Q${f.quarter}`, amsm_am_equity: f.amsm_am_equity / 100, amsm_am_profit: f.amsm_am_profit / 100,
        fo_pct_amsm: f.fo_pct_amsm / 100, fo_pct_am: f.fo_pct_am / 100,
        owner_codes: f.owner_codes.map((c) => ({ code: c.code.trim(), pot: c.pot, meaning: c.meaning || "", am_equity: c.am_equity / 100, am_profit: c.am_profit / 100 })),
      });
      toast.success("Settings saved – all figures recalculated"); reload(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="settings-tab">
      <Panel eyebrow="Settings" title="Splits & report window" actions={<Button onClick={save} disabled={busy} data-testid="settings-save-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save settings</Button>}>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <PctField label="AMSM – AM share of equity" value={f.amsm_am_equity} onChange={(v) => setF({ ...f, amsm_am_equity: v })} testid="settings-amsm-equity" hint={`SM gets ${100 - f.amsm_am_equity}%`} />
          <PctField label="AMSM – AM share of profit (before FO)" value={f.amsm_am_profit} onChange={(v) => setF({ ...f, amsm_am_profit: v })} testid="settings-amsm-profit" hint={`SM gets ${100 - f.amsm_am_profit}%`} />
          <PctField label="Family Office % of AM profit – AMSM" value={f.fo_pct_amsm} onChange={(v) => setF({ ...f, fo_pct_amsm: v })} testid="settings-fo-amsm" />
          <PctField label="Family Office % of AM profit – AM" value={f.fo_pct_am} onChange={(v) => setF({ ...f, fo_pct_am: v })} testid="settings-fo-am" />
          <div className="space-y-1.5"><Label className="text-xs">Report window starts (24 quarters)</Label>
            <div className="flex gap-2">
              <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.quarter} onChange={(e) => setF({ ...f, quarter: e.target.value })} data-testid="settings-window-quarter">{[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}</select>
              <select className="h-9 flex-1 rounded-md border bg-background px-2 text-sm" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} data-testid="settings-window-year">{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
            </div></div>
        </div>
        <h3 className="mt-8 mb-3 font-heading text-sm font-bold">Owner codes</h3>
        <div className="overflow-x-auto grid-scroll">
          <table className="w-full text-sm" data-testid="owner-codes-table">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-2">Code</th><th className="py-2 px-2">Pot</th><th className="py-2 px-2">Default AM equity %</th><th className="py-2 px-2">Default AM profit %</th><th className="py-2 pl-2">Meaning</th></tr></thead>
            <tbody>{f.owner_codes.map((c, i) => {
              const linked = c.pot === "AMSM";
              return (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 pr-2"><Input value={c.code} onChange={(e) => setCode(i, "code", e.target.value)} className="w-28" data-testid={`owner-code-${i}-code`} /></td>
                  <td className="py-2 px-2"><select className="h-9 rounded-md border bg-background px-2" value={c.pot} onChange={(e) => setCode(i, "pot", e.target.value)} data-testid={`owner-code-${i}-pot`}><option>AMSM</option><option>AM</option></select></td>
                  <td className="py-2 px-2"><Input type="number" step="any" disabled={linked} value={linked ? f.amsm_am_equity : c.am_equity} onChange={(e) => setCode(i, "am_equity", e.target.value)} className="w-24" data-testid={`owner-code-${i}-equity`} /></td>
                  <td className="py-2 px-2"><Input type="number" step="any" disabled={linked} value={linked ? f.amsm_am_profit : c.am_profit} onChange={(e) => setCode(i, "am_profit", e.target.value)} className="w-24" data-testid={`owner-code-${i}-profit`} /></td>
                  <td className="py-2 pl-2"><Input value={c.meaning} onChange={(e) => setCode(i, "meaning", e.target.value)} className="min-w-[220px]" /></td>
                </tr>);
            })}</tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => setF({ ...f, owner_codes: [...f.owner_codes, { code: "", pot: "AM", am_equity: "100", am_profit: "100", meaning: "" }] })} data-testid="add-owner-code-btn"><Plus className="mr-1 h-4 w-4" /> Add owner code</Button>
        <p className="mt-3 text-xs text-muted-foreground">AMSM codes follow the AMSM split above. Per-project overrides (e.g. AM/FN projects) are set on the project page.</p>
      </Panel>
      <PartnerList partners={data.partners} reload={reload} />
    </div>
  );
}
